import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';

import { KontrollterminService, Kontrolltermin } from '../services/kontrolltermin.service';
import { MassnahmeService, Massnahme } from '../services/massnahme.service';
import { MitarbeiterService, Mitarbeiter } from '../services/mitarbeiter.service';
import { StandortService, Standort } from '../services/standort.service';
import { KundeService } from '../services/kunde.service';
import { environment } from '../../environments/environment';

type Step =
  | 'geplant'
  | 'aktuell'
  | 'bearbeitung'
  | 'freigabe'
  | 'abgeschlossen'
  | 'others';

@Component({
  selector: 'app-kontrolltermine',
  templateUrl: './kontrolltermine.component.html',
  styleUrls: ['./kontrolltermine.component.scss']
})
export class KontrolltermineComponent implements OnInit, OnDestroy {
  isLoading = false;
  // Tab management - Freigabe is the default (index 3)
  activeTabIndex = 3;

  // Tab data
  kontrolltermine: Kontrolltermin[] = [];
  massnahmen: Massnahme[] = [];
  
  // Loading states for tabs
  kontrolltermineLoading = false;
  massnahmenLoading = false;

  // Search and filters
  searchTerm = '';
  selectedProjektaufgabeId = '';
  selectedStatusId = '';
  selectedKundeId = '';

  // New search and filter properties for autocomplete
  kundeSearchValue = '';
  verantwortlicherSearchValue = '';
  statusSearchValue = '';
  
  // Selected filter values
  selectedKunde: any = null;
  selectedVerantwortlicher: any = null;
  selectedStatus: any = null;
  
  // Autocomplete visibility flags
  showKundeAutocomplete = false;
  showStatusAutocomplete = false;
  showStandortAutocomplete = false;
  
  // Others dropdown
  showOthersDropdown = false;
  isOthersActive = false; // Track if we're viewing others data
  
  // Status tracking for edit navigation
  currentActiveStatus: string = 'freigabe'; // Track current status
  
  // Filtered autocomplete results
  filteredKunden: any[] = [];
  filteredStatus: any[] = [];
  filteredStandorte: any[] = [];

  // Pagination
  currentPage = 1;
  pageSize = 10;
  totalItems = 0;
  totalPages = 0;

  // Dropdown data
  projektaufgaben: any[] = [];
  status: any[] = [];
  kunden: any[] = [];
  techniker: Mitarbeiter[] = [];
  standorte: Standort[] = [];
  
  // Month filter data
  months = [
    { value: '01', label: 'Januar' },
    { value: '02', label: 'Februar' },
    { value: '03', label: 'März' },
    { value: '04', label: 'April' },
    { value: '05', label: 'Mai' },
    { value: '06', label: 'Juni' },
    { value: '07', label: 'Juli' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Dezember' }
  ];
  
  // Selected filter values
  selectedMonth = '';
  selectedStandort: any = null;
  standortSearchValue = '';

  // Others dropdown options
  othersOptions = [
    { value: 'geloescht', label: 'Gelöscht', description: 'Gelöschte Kontrolltermine' },
    { value: 'abgebrochen', label: 'Abgebrochen', description: 'Abgebrochene Kontrolltermine' },
    { value: 'fehlerhaft', label: 'Fehlerhaft', description: 'Fehlerhafte Kontrolltermine' }
  ];

  // Table columns - update to show new fields
  displayedColumns = [
    'einsatznummer', 
    'standort', 
    'projektaufgabe', 
    'kunde', 
    'techniker', 
    'einsatztyp', 
    'datum_durchfuerung', 
    'status', 
    'global_befall', 
    'actions'
  ];

 // Tab order is tied to this array's index
readonly steps: Step[] = [
  'geplant',
  'aktuell',
  'bearbeitung',
  'freigabe',
  'abgeschlossen',
];

// Programmatic selection - Freigabe is the default (index 3)
selectedIndex = signal(3);

select(step: Step) {
  const idx = this.steps.indexOf(step);
  if (idx >= 0) {
    this.selectedIndex.set(idx);
    this.activeTabIndex = idx;
    this.isOthersActive = false; // Deactivate others when regular tab is selected
    this.currentActiveStatus = step; // Track current status
    this.loadDataForStep(step);
  }
}

// For a11y (aria-selected)
isSelected(step: Step) {
  if (step === 'others') {
    // Check if others dropdown is open to show the indicator
    return this.showOthersDropdown;
  }
  // If others is active, no regular tab should be selected
  if (this.isOthersActive) {
    return false;
  }
  return this.steps[this.selectedIndex()] === step;
}

/**
 * Load data based on selected step
 */
loadDataForStep(step: Step) {
  switch (step) {
    case 'geplant':
      this.loadKontrolltermineByStatus('geplant');
      break;
    case 'aktuell':
      this.loadKontrolltermineByStatus('aktuell');
      break;
    case 'bearbeitung':
      this.loadKontrolltermineByStatus('bearbeitung');
      break;
    case 'freigabe':
      this.loadKontrolltermineByStatus('freigabe');
      break;
    case 'abgeschlossen':
      this.loadKontrolltermineByStatus('abgeschlossen');
      break;
    case 'others':
      // This is now handled by the dropdown, so we don't load data here
      break;
  }
}

/**
 * Load kontrolltermine filtered by status
 */
loadKontrolltermineByStatus(status: string) {
  this.kontrolltermineLoading = true;
  
  // Map status to status_id for filtering
  let statusId: number | undefined;
  switch (status) {
    case 'geplant':
      statusId = 4; // geplant
      break;
    case 'aktuell':
      // Aktuell is a filter for geplant kontrolltermine within 30 days
      // We'll handle this with date filtering instead of status_id
      statusId = 4; // geplant
      break;
    case 'bearbeitung':
      statusId = 6; // in_bearbeitung
      break;
    case 'freigabe':
      statusId = 7; // freigabe
      break;
    case 'abgeschlossen':
      statusId = 8; // abgeschlossen
      break;
    case 'geloescht':
      statusId = 1; // geloescht
      break;
    case 'abgebrochen':
      statusId = 2; // abgebrochen
      break;
    case 'fehlerhaft':
      statusId = 3; // fehlerhaft
      break;
    default:
      statusId = undefined;
  }
  
  // For aktuell, we need to filter by date (next 30 days)
  let dateFrom: string | undefined;
  let dateTo: string | undefined;
  
  if (status === 'aktuell') {
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);
    
    dateFrom = today.toISOString().split('T')[0]; // YYYY-MM-DD
    dateTo = thirtyDaysFromNow.toISOString().split('T')[0]; // YYYY-MM-DD
    
    console.log('Aktuell filter: dateFrom =', dateFrom, 'dateTo =', dateTo);
  }

  this.kontrollterminService.getKontrolltermine(
    statusId,
    this.selectedMonth,
    this.selectedStandort?.id,
    this.selectedKunde?.id, // Apply kunde filter
    this.selectedVerantwortlicher?.id, // Apply techniker filter
    undefined, // einsatztyp
    dateFrom,
    dateTo,
    this.searchTerm, // Apply search term
    this.currentPage, // Apply pagination
    this.pageSize // Apply page size
  ).subscribe({
    next: (response) => {
      console.log('Kontrolltermine loaded:', response);
      if (response.length > 0) {
        console.log('First kontrolltermin data:', response[0]);
        console.log('Kunde data:', response[0].kunde);
        console.log('Standort data:', response[0].standort);
        console.log('Techniker data:', response[0].techniker);
      }
      this.kontrolltermine = response;
      
      // Handle pagination - if API returns pagination metadata, use it
      // Otherwise, calculate from response length (client-side pagination)
      if (response && typeof response === 'object' && 'data' in response && 'total' in response) {
        // Server-side pagination response
        const paginatedResponse = response as { data: Kontrolltermin[], total: number };
        this.kontrolltermine = paginatedResponse.data;
        this.totalItems = paginatedResponse.total;
        this.totalPages = Math.ceil(paginatedResponse.total / this.pageSize);
      } else {
        // Client-side pagination (current behavior)
        this.kontrolltermine = response as Kontrolltermin[];
        this.totalItems = response.length;
        this.totalPages = Math.ceil(response.length / this.pageSize);
      }
      
      
      this.kontrolltermineLoading = false;
    },
    error: (error) => {
      console.error('Error loading kontrolltermine:', error);
      this.snackBar.open('Fehler beim Laden der Kontrolltermine', 'Schließen', { duration: 3000 });
      this.kontrolltermineLoading = false;
    }
  });
}
  
  private destroy$ = new Subscription();

  constructor(
    private kontrollterminService: KontrollterminService,
    private massnahmeService: MassnahmeService,
    private mitarbeiterService: MitarbeiterService,
    private standortService: StandortService,
    private kundeService: KundeService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    // Load only dropdown data initially, not kontrolltermine
    this.loadDropdowns();
    // Set Freigabe as the default active tab - this will load the correct data
    this.select('freigabe');
  }

  ngOnDestroy() {
    this.destroy$.unsubscribe();
  }

  /**
   * Load initial data
   */
  loadInitialData() {
    this.loadKontrolltermine();
    this.loadDropdowns();
  }

  /**
   * Load kontrolltermine with pagination and filters
   */
  loadKontrolltermine() {
    this.kontrolltermineLoading = true;
    this.kontrollterminService.getKontrolltermine(
      this.selectedStatusId ? parseInt(this.selectedStatusId) : undefined,
      this.selectedMonth,
      this.selectedStandort?.id
    ).subscribe({
      next: (response) => {
        this.kontrolltermine = response;
        this.totalItems = response.length;
        this.totalPages = Math.ceil(response.length / this.pageSize);
        
        
        this.kontrolltermineLoading = false;
      },
      error: (error) => {
        console.error('Error loading kontrolltermine:', error);
        this.snackBar.open('Fehler beim Laden der Kontrolltermine', 'Schließen', { duration: 3000 });
        this.kontrolltermineLoading = false;
      }
    });
  }

  /**
   * Load massnahmen
   */
  loadMassnahmen() {
    this.massnahmenLoading = true;
    this.massnahmeService.getMassnahmen().subscribe({
      next: (response) => {
        this.massnahmen = response.data;
        this.massnahmenLoading = false;
      },
      error: (error) => {
        console.error('Error loading massnahmen:', error);
        this.snackBar.open('Fehler beim Laden der Maßnahmen', 'Schließen', { duration: 3000 });
        this.massnahmenLoading = false;
      }
    });
  }

  /**
   * Load dropdown data
   */
  loadDropdowns() {
    // Load techniker data
    this.mitarbeiterService.getTechnikerList().subscribe({
      next: (response) => {
        // Sort techniker alphabetically by nachname, then vorname
        this.techniker = response.data.sort((a, b) => {
          const nameA = `${a.nachname} ${a.vorname}`.toLowerCase();
          const nameB = `${b.nachname} ${b.vorname}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
      }
    });

    // Load standorte data
    this.standortService.getStandorte().subscribe({
      next: (response) => {
        this.standorte = response.data || [];
      },
      error: (error) => {
        console.error('Error loading standorte:', error);
      }
    });

    // Load kunden data
    this.kundeService.getKunden(1, 200).subscribe({
      next: (response) => {
        this.kunden = response.data || [];
        console.log('Loaded kunden for filter:', this.kunden.length);
      },
      error: (error) => {
        console.error('Error loading kunden:', error);
      }
    });
  }

  /**
   * Handle tab change
   */
  onTabChange(event: MatTabChangeEvent) {
    this.activeTabIndex = event.index;
    
    if (this.activeTabIndex === 0) {
      this.loadKontrolltermine();
    } else if (this.activeTabIndex === 1) {
      this.loadMassnahmen();
    }
  }

  /**
   * Search kontrolltermine
   */
  onSearch() {
    this.currentPage = 1;
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Clear search
   */
  clearSearch() {
    this.searchTerm = '';
    this.currentPage = 1;
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Apply filters
   */
  applyFilters() {
    this.currentPage = 1;
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Clear all filters
   */
  clearFilters() {
    this.selectedProjektaufgabeId = '';
    this.selectedStatusId = '';
    this.selectedKundeId = '';
    this.selectedKunde = null;
    this.selectedVerantwortlicher = null;
    this.selectedStatus = null;
    this.selectedMonth = '';
    this.selectedStandort = null;
    this.kundeSearchValue = '';
    this.verantwortlicherSearchValue = '';
    this.statusSearchValue = '';
    this.standortSearchValue = '';
    this.currentPage = 1;
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }

  /**
   * Handle techniker selection
   */
  onTechnikerChange(technikerId: string) {
    this.selectedVerantwortlicher = technikerId ? this.techniker.find(t => t.id.toString() === technikerId) : null;
    this.verantwortlicherSearchValue = this.selectedVerantwortlicher ? 
      `${this.selectedVerantwortlicher.vorname} ${this.selectedVerantwortlicher.nachname}` : '';
    this.applyFilters();
  }

  /**
   * Change page
   */
  onPageChange(page: number) {
    this.currentPage = page;
    // Load data for current selected step
    const currentStep = this.steps[this.selectedIndex()];
    this.loadDataForStep(currentStep);
  }


  /**
   * Edit kontrolltermin
   */
  editKontrolltermin(kontrolltermin: Kontrolltermin, event?: Event) {
    if (event) {
      event.stopPropagation(); // Prevent row click
    }
    
    // Get the actual status of the kontrolltermin
    const actualStatus = this.getStatusKeyFromId(kontrolltermin.status_id);
    
    // For aktuell tab, use the actual status (geplant) instead of the tab status
    const sourceStatus = (this.currentActiveStatus === 'aktuell' && actualStatus === 'geplant') 
      ? 'geplant' 
      : this.currentActiveStatus;
    
    // Console logging for status tracking
    console.log('🔍 EDIT TRIGGERED:');
    console.log('📊 Current Active Status:', this.currentActiveStatus);
    console.log('📋 Kontrolltermin Status ID:', kontrolltermin.status_id);
    console.log('📋 Kontrolltermin Status Name:', this.getStatusLabel(kontrolltermin.status_id));
    console.log('📋 Actual Status:', actualStatus);
    console.log('📋 Source Status (used):', sourceStatus);
    console.log('🎯 Status Match:', this.currentActiveStatus === actualStatus);
    console.log('🌐 Navigating to:', `/kontrolltermine/${kontrolltermin.id}?mode=edit&sourceStatus=${sourceStatus}`);
    
    // Pass the correct source status as query parameter
    this.router.navigate(['/kontrolltermine', kontrolltermin.id], { 
      queryParams: { 
        mode: 'edit',
        sourceStatus: sourceStatus // Use actual status for aktuell tab
      } 
    });
  }

  /**
   * Delete kontrolltermin
   */
  deleteKontrolltermin(kontrolltermin: Kontrolltermin) {
    // TODO: Implement delete confirmation
    this.snackBar.open(`Löschen: ${kontrolltermin.id}`, 'Schließen', { duration: 2000 });
  }

  /**
   * View kontrolltermin details
   */
  viewKontrolltermin(kontrolltermin: Kontrolltermin) {
    console.log('👁️ VIEW TRIGGERED:');
    console.log('📊 Current Active Status:', this.currentActiveStatus);
    console.log('📋 Kontrolltermin Status ID:', kontrolltermin.status_id);
    
    this.router.navigate(['/kontrolltermine', kontrolltermin.id], { 
      queryParams: { 
        mode: 'view',
        sourceStatus: this.currentActiveStatus // Pass current status
      } 
    });
  }

  /**
   * Create new massnahme
   */
  createMassnahme() {
    // TODO: Implement create dialog
    this.snackBar.open('Erstellen-Funktion wird implementiert', 'Schließen', { duration: 2000 });
  }

  /**
   * Edit massnahme
   */
  editMassnahme(massnahme: Massnahme) {
    // TODO: Implement edit dialog
    this.snackBar.open(`Bearbeiten: ${massnahme.beschreibung}`, 'Schließen', { duration: 2000 });
  }

  /**
   * Delete massnahme
   */
  deleteMassnahme(massnahme: Massnahme) {
    // TODO: Implement delete confirmation
    this.snackBar.open(`Löschen: ${massnahme.beschreibung}`, 'Schließen', { duration: 2000 });
  }

  /**
   * Get CSS class for status badge
   */
  getStatusClass(statusCode?: string): string {
    if (!statusCode) return 'status-badge inactive';
    
    switch (statusCode.toLowerCase()) {
      case 'active':
      case 'aktiv':
        return 'status-badge active';
      case 'completed':
      case 'abgeschlossen':
        return 'status-badge completed';
      case 'pending':
      case 'ausstehend':
        return 'status-badge pending';
      default:
        return 'status-badge inactive';
    }
  }

  // New autocomplete methods for search and filter functionality

  /**
   * Kunde search and filter methods
   */
  onKundeSearch(event: any) {
    const value = event.target.value;
    this.kundeSearchValue = value;
    
    if (value.length > 0) {
      this.filteredKunden = this.kunden.filter(kunde => 
        kunde.suchbegriff?.toLowerCase().includes(value.toLowerCase()) ||
        kunde.name?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.filteredKunden = [];
    }
  }

  onKundeBlur() {
    setTimeout(() => {
      this.showKundeAutocomplete = false;
    }, 200);
  }

  selectKunde(kunde: any) {
    this.selectedKunde = kunde;
    this.kundeSearchValue = kunde.suchbegriff || kunde.name;
    this.showKundeAutocomplete = false;
    this.applyFilters();
  }

  clearKundeFilter() {
    this.selectedKunde = null;
    this.kundeSearchValue = '';
    this.applyFilters();
  }

  getKundeDisplayValue(): string {
    if (this.selectedKunde) {
      return this.selectedKunde.suchbegriff || this.selectedKunde.name;
    }
    return this.kundeSearchValue;
  }

  /**
   * Clear techniker filter
   */
  clearVerantwortlicherFilter() {
    this.selectedVerantwortlicher = null;
    this.verantwortlicherSearchValue = '';
    this.applyFilters();
  }

  /**
   * Status search and filter methods
   */
  onStatusSearch(event: any) {
    const value = event.target.value;
    this.statusSearchValue = value;
    
    if (value.length > 0) {
      this.filteredStatus = this.status.filter(status => 
        status.beschreibung?.toLowerCase().includes(value.toLowerCase()) ||
        status.code?.toLowerCase().includes(value.toLowerCase())
      );
    } else {
      this.filteredStatus = [];
    }
  }

  onStatusBlur() {
    setTimeout(() => {
      this.showStatusAutocomplete = false;
    }, 200);
  }

  selectStatus(status: any) {
    this.selectedStatus = status;
    this.statusSearchValue = status.beschreibung;
    this.showStatusAutocomplete = false;
    this.applyFilters();
  }

  clearStatusFilter() {
    this.selectedStatus = null;
    this.statusSearchValue = '';
    this.applyFilters();
  }

  getStatusDisplayValue(): string {
    if (this.selectedStatus) {
      return this.selectedStatus.beschreibung;
    }
    return this.statusSearchValue;
  }

  /**
   * Month filter methods
   */
  onMonthChange(monthValue: string) {
    this.selectedMonth = monthValue;
    this.applyFilters();
  }

  /**
   * Standort search and filter methods
   */
  onStandortSearch(event: any) {
    const value = event.target.value;
    this.standortSearchValue = value;
    
    if (value.length > 0) {
      this.filteredStandorte = this.standorte.filter(standort => 
        standort.name?.toLowerCase().includes(value.toLowerCase()) ||
        standort.nummer?.toString().includes(value)
      );
    } else {
      this.filteredStandorte = [];
    }
  }

  onStandortBlur() {
    setTimeout(() => {
      this.showStandortAutocomplete = false;
    }, 200);
  }

  selectStandort(standort: any) {
    this.selectedStandort = standort;
    this.standortSearchValue = standort.name;
    this.showStandortAutocomplete = false;
    this.applyFilters();
  }

  clearStandortFilter() {
    this.selectedStandort = null;
    this.standortSearchValue = '';
    this.applyFilters();
  }

  getStandortDisplayValue(): string {
    if (this.selectedStandort) {
      return this.selectedStandort.name;
    }
    return this.standortSearchValue;
  }

  /**
   * Get CSS classes for status badge based on status_id
   */
  getStatusClasses(statusId: number): string {
    switch (statusId) {
      case 1: // geloescht
        return 'bg-gray-50 text-gray-700 ring-gray-600/20 dark:bg-gray-900/30 dark:text-gray-400 dark:ring-gray-500/50';
      case 2: // abgebrochen
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 3: // fehlerhaft
        return 'bg-red-50 text-red-700 ring-red-600/20 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-500/50';
      case 4: // geplant
        return 'bg-blue-50 text-blue-700 ring-blue-600/20 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-500/50';
      case 5: // aktuell
        return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20 dark:bg-yellow-900/30 dark:text-yellow-400 dark:ring-yellow-500/50';
      case 6: // in_bearbeitung
        return 'bg-orange-50 text-orange-700 ring-orange-600/20 dark:bg-orange-900/30 dark:text-orange-400 dark:ring-orange-500/50';
      case 7: // freigabe
        return 'bg-purple-50 text-purple-700 ring-purple-600/20 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-500/50';
      case 8: // abgeschlossen
        return 'bg-green-50 text-green-700 ring-green-600/20 dark:bg-green-900/30 dark:text-green-400 dark:ring-green-500/50';
      default:
        return 'bg-slate-50 text-slate-700 ring-slate-600/20 dark:bg-slate-900/30 dark:text-slate-400 dark:ring-slate-500/50';
    }
  }

  /**
   * Get status label by status_id
   */
  getStatusLabel(statusId: number): string {
    const status = environment.kontrolltermin_status.find(s => s.id === statusId);
    return status?.label || 'Unbekannt';
  }

  /**
   * Get befall label for tooltip
   */
  getBefallLabel(befall: number | null | undefined): string {
    if (befall === 1) return 'Befall vorhanden';
    if (befall === 0) return 'Kein Befall';
    return 'Unbekannt';
  }

  /**
   * Show others dropdown
   */
  openOthersDropdown() {
    this.showOthersDropdown = true;
  }

  /**
   * Hide others dropdown
   */
  hideOthersDropdown() {
    this.showOthersDropdown = false;
  }

  /**
   * Handle others option selection
   */
  selectOthersOption(value: string) {
    this.hideOthersDropdown();
    this.isOthersActive = true; // Keep others button active
    this.currentActiveStatus = value; // Track current status
    
    // Map the selected value to the appropriate status and load data
    switch (value) {
      case 'geloescht':
        this.loadKontrolltermineByStatus('geloescht');
        break;
      case 'abgebrochen':
        this.loadKontrolltermineByStatus('abgebrochen');
        break;
      case 'fehlerhaft':
        this.loadKontrolltermineByStatus('fehlerhaft');
        break;
    }
  }

  /**
   * Get status key from status ID
   */
  getStatusKeyFromId(statusId: number): string {
    const statusMap: {[key: number]: string} = {
      1: 'geloescht',
      2: 'abgebrochen', 
      3: 'fehlerhaft',
      4: 'geplant',
      5: 'aktuell',
      6: 'bearbeitung',
      7: 'freigabe',
      8: 'abgeschlossen'
    };
    return statusMap[statusId] || 'unknown';
  }

}
